/**
 * Deviation Service - Handles all discrepancy calculations and management
 * Integrates with main business data for centralized storage
 */

import { sumCashSales } from '../utils/saleSessionHelpers';
import { toFiniteNumber, outflowMagnitude } from '../domain/finance/sessionCashflow';

class DeviationService {
  /**
   * Évaluation à partir du détail central (buildSessionDeviationDetail) — pas de recalcul séparé.
   */
  static deviationFromSessionDetail(detail) {
    if (!detail || typeof detail !== 'object') {
      return {
        expectedAmount: 0,
        actualAmount: 0,
        difference: 0,
        expectedElectronicAmount: 0,
        actualElectronicAmount: 0,
        electronicDifference: 0,
        expectedTotalAmount: 0,
        actualTotalAmount: 0,
        totalDifference: 0,
        hasDeviation: false,
        withinTolerance: true,
        type: 'exact'
      };
    }

    const tolerance = 0.01;
    const difference = toFiniteNumber(detail.difference, 0);
    const electronicDifference = toFiniteNumber(detail.electronicDifference, 0);
    const totalDifference = toFiniteNumber(
      detail.totalDifference,
      difference + electronicDifference
    );
    const initialElectronic = toFiniteNumber(detail.initialElectronicAmount, 0);
    const electronicSales = toFiniteNumber(detail.electronicSales, 0);
    const actualElectronicAmount = toFiniteNumber(detail.actualElectronicAmount, 0);
    const hasElectronicActivity =
      Math.abs(initialElectronic) > tolerance ||
      Math.abs(electronicSales) > tolerance ||
      Math.abs(actualElectronicAmount) > tolerance;
    const cashDeviation = Math.abs(difference) > tolerance;
    const electronicDeviation =
      hasElectronicActivity && Math.abs(electronicDifference) > tolerance;
    const hasDeviation =
      detail.totalDifference != null && Number.isFinite(Number(detail.totalDifference))
        ? Math.abs(totalDifference) > tolerance
        : cashDeviation || electronicDeviation;

    return {
      ...detail,
      expectedAmount: toFiniteNumber(detail.expectedAmount, 0),
      actualAmount: toFiniteNumber(detail.actualAmount ?? detail.finalAmount, 0),
      difference,
      expectedElectronicAmount: toFiniteNumber(detail.expectedElectronicAmount, 0),
      actualElectronicAmount,
      electronicDifference,
      expectedTotalAmount:
        toFiniteNumber(detail.expectedAmount, 0) +
        toFiniteNumber(detail.expectedElectronicAmount, 0),
      actualTotalAmount:
        toFiniteNumber(detail.actualAmount ?? detail.finalAmount, 0) + actualElectronicAmount,
      totalDifference,
      hasDeviation,
      withinTolerance: !hasDeviation,
      type: !hasDeviation
        ? 'exact'
        : totalDifference < -tolerance
          ? 'shortage'
          : totalDifference > tolerance
            ? 'surplus'
            : 'exact',
      calculatedAt: new Date().toISOString()
    };
  }

  /**
   * Calculate Type 1: Self-calculation deviation
   * Formula: (Initial + Cash Sales − Cash Outflows) vs Logout Amount
   */
  static calculateSelfDeviation(sessionData) {
    try {
      // 1. Input-Validierung
      if (!sessionData || typeof sessionData !== 'object') {
        throw new Error('Invalid session data provided');
      }

      const { initialAmount, initialElectronicAmount, finalAmount, sales } = sessionData;
      
      // 2. Robuste Zahlen-Validierung
      const initial = toFiniteNumber(initialAmount, 0);
      const initialElectronic = toFiniteNumber(initialElectronicAmount, 0);
      const final = toFiniteNumber(finalAmount, 0);
      const salesArray = Array.isArray(sales) ? sales : [];
      const cashSales =
        sessionData.cashSales != null && Number.isFinite(Number(sessionData.cashSales))
          ? Number(sessionData.cashSales)
          : sumCashSales(salesArray);
      const electronicSales = toFiniteNumber(sessionData.electronicSales, 0);
      const cashOutgoing = outflowMagnitude(sessionData.cashOutgoing);
      const electronicOutgoing = outflowMagnitude(sessionData.electronicOutgoing);
      const cashPaymentsOutgoing = outflowMagnitude(sessionData.cashPaymentsOutgoing);
      const cashExpensesOutgoing = outflowMagnitude(sessionData.cashExpensesOutgoing);
      const electronicPaymentsOutgoing = outflowMagnitude(sessionData.electronicPaymentsOutgoing);
      const electronicExpensesOutgoing = outflowMagnitude(sessionData.electronicExpensesOutgoing);
      const cashExternalOutgoing = outflowMagnitude(sessionData.cashExternalOutgoing);
      const electronicExternalOutgoing = outflowMagnitude(sessionData.electronicExternalOutgoing);
      const managerCashOutgoing = outflowMagnitude(sessionData.managerCashOutgoing);
      const managerElectronicOutgoing = outflowMagnitude(sessionData.managerElectronicOutgoing);
      const cashPaymentsExternal = outflowMagnitude(sessionData.cashPaymentsExternal);
      const cashExpensesExternal = outflowMagnitude(sessionData.cashExpensesExternal);
      const electronicPaymentsExternal = outflowMagnitude(sessionData.electronicPaymentsExternal);
      const electronicExpensesExternal = outflowMagnitude(sessionData.electronicExpensesExternal);
      const electronicSalesByProvider =
        sessionData.electronicSalesByProvider && typeof sessionData.electronicSalesByProvider === 'object'
          ? sessionData.electronicSalesByProvider
          : {};
      
      // Soll = Anfang + Einnahmen − Ausgänge (Magnitude, nie doppelt negieren)
      const expectedAmount =
        sessionData.expectedCash != null && Number.isFinite(Number(sessionData.expectedCash))
          ? toFiniteNumber(sessionData.expectedCash, initial + cashSales - cashOutgoing)
          : initial + cashSales - cashOutgoing;
      const difference = final - expectedAmount;
      const expectedElectronicAmount =
        sessionData.expectedElectronic != null && Number.isFinite(Number(sessionData.expectedElectronic))
          ? toFiniteNumber(sessionData.expectedElectronic, initialElectronic + electronicSales - electronicOutgoing)
          : initialElectronic + electronicSales - electronicOutgoing;
      const actualElectronicAmount = toFiniteNumber(sessionData.finalElectronicAmount, 0);
      const electronicDifference = actualElectronicAmount - expectedElectronicAmount;
      const expectedTotalAmount = expectedAmount + expectedElectronicAmount;
      const actualTotalAmount = final + actualElectronicAmount;
      const totalDifference = actualTotalAmount - expectedTotalAmount;
      const tolerance = 0.01;
      const hasElectronicActivity =
        Math.abs(initialElectronic) > tolerance ||
        Math.abs(electronicSales) > tolerance ||
        Math.abs(actualElectronicAmount) > tolerance;
      const cashDeviation = Math.abs(difference) > tolerance;
      const electronicDeviation =
        hasElectronicActivity && Math.abs(electronicDifference) > tolerance;
      const hasDeviation = cashDeviation || electronicDeviation;
      
      // 5. Wenn innerhalb der Toleranz, keine Deviation erstellen
      if (!hasDeviation) {
        return {
          expectedAmount,
          actualAmount: final,
          difference: 0,
          expectedElectronicAmount,
          actualElectronicAmount,
          electronicDifference: 0,
          expectedTotalAmount,
          actualTotalAmount,
          totalDifference: 0,
          hasDeviation: false,
          type: 'exact',
        cashSales,
        electronicSales,
        cashOutgoing,
        electronicOutgoing,
        cashPaymentsOutgoing,
        cashExpensesOutgoing,
        electronicPaymentsOutgoing,
        electronicExpensesOutgoing,
        cashExternalOutgoing,
        electronicExternalOutgoing,
        managerCashOutgoing,
        managerElectronicOutgoing,
        cashPaymentsExternal,
        cashExpensesExternal,
        electronicPaymentsExternal,
        electronicExpensesExternal,
        electronicSalesByProvider,
        initialAmount: initial,
        initialElectronicAmount: initialElectronic,
        finalAmount: final,
        withinTolerance: true
      };
      }
      
      return {
        expectedAmount,
        actualAmount: final,
        difference,
        expectedElectronicAmount,
        actualElectronicAmount,
        electronicDifference,
        expectedTotalAmount,
        actualTotalAmount,
        totalDifference,
        hasDeviation: true,
        type: totalDifference < 0 ? 'shortage' : 'surplus',
        cashSales,
        electronicSales,
        cashOutgoing,
        electronicOutgoing,
        cashPaymentsOutgoing,
        cashExpensesOutgoing,
        electronicPaymentsOutgoing,
        electronicExpensesOutgoing,
        cashExternalOutgoing,
        electronicExternalOutgoing,
        managerCashOutgoing,
        managerElectronicOutgoing,
        cashPaymentsExternal,
        cashExpensesExternal,
        electronicPaymentsExternal,
        electronicExpensesExternal,
        electronicSalesByProvider,
        initialAmount: initial,
        initialElectronicAmount: initialElectronic,
        finalAmount: final,
        tolerance,
        calculatedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Deviation calculation failed:', error);
      // Fallback bei Fehlern
      return {
        expectedAmount: 0,
        actualAmount: 0,
        difference: 0,
        expectedElectronicAmount: 0,
        actualElectronicAmount: 0,
        electronicDifference: 0,
        expectedTotalAmount: 0,
        actualTotalAmount: 0,
        totalDifference: 0,
        hasDeviation: false,
        type: 'error',
        cashSales: 0,
        electronicSales: 0,
        electronicSalesByProvider: {},
        initialAmount: 0,
        initialElectronicAmount: 0,
        finalAmount: 0,
        tolerance: 0.01,
        error: error.message,
        calculatedAt: new Date().toISOString()
      };
    }
  }

  /**
   * Calculate Type 2: Handover deviation
   * Formula: (Previous Worker's Reported Amount) - (Next Worker's Found Amount)
   */
  static calculateHandoverDeviation(previousAmount, foundAmount) {
    const difference = foundAmount - previousAmount; // Umgekehrte Logik: gefunden - gemeldet
    
    return {
      reportedAmount: previousAmount,
      foundAmount,
      difference,
      type: difference === 0 ? 'exact' : (difference < 0 ? 'shortage' : 'surplus')
    };
  }

  /**
   * Create self deviation record - Only if there's a real deviation
   */
  static createSelfDeviation(sessionData, deviation, explanation = '') {
    // Nur echte Deviationen erstellen (nicht innerhalb der Toleranz)
    if (!deviation.hasDeviation || deviation.withinTolerance) {
      return null;
    }
    
    return {
      id: `self_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'self',
      sessionId: sessionData.sessionId,
      workerId: sessionData.workerId,
      workerName: sessionData.workerName,
      timestamp: new Date().toISOString(),
      deviation,
      explanation,
      status: 'pending', // 'pending', 'accepted', 'rejected'
      managerReview: {
        reviewedBy: null,
        reviewedAt: null,
        decision: null,
        comments: ''
      }
    };
  }

  /**
   * Create handover deviation record
   */
  static createHandoverDeviation(previousWorkerId, previousWorkerName, nextWorkerId, nextWorkerName, deviation, explanation = '') {
    const amount = Math.abs(
      Number(deviation?.totalDifference ?? deviation?.difference ?? 0) || 0
    );
    if (amount <= 0.01) {
      return null;
    }

    return {
      id: `handover_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'handover',
      previousWorkerId,
      previousWorkerName,
      nextWorkerId,
      nextWorkerName,
      timestamp: new Date().toISOString(),
      deviation,
      explanation,
      status: 'pending', // 'pending', 'accepted', 'rejected'
      managerReview: {
        reviewedBy: null,
        reviewedAt: null,
        decision: null,
        comments: ''
      }
    };
  }

  /**
   * Check if worker exceeds monthly tolerance
   */
  static checkToleranceExceeded(workerId, currentMonth, workerTolerances, newDeviationAmount) {
    const toleranceKey = `${workerId}_${currentMonth}`;
    const tolerance = workerTolerances[toleranceKey];
    
    if (!tolerance) {
      return { exceeded: false, currentUsed: 0, limit: 0 };
    }
    
    const newTotal = tolerance.currentUsed + Math.abs(newDeviationAmount);
    const exceeded = newTotal > tolerance.monthlyLimit;
    
    return {
      exceeded,
      currentUsed: tolerance.currentUsed,
      newTotal,
      limit: tolerance.monthlyLimit,
      remaining: tolerance.monthlyLimit - tolerance.currentUsed
    };
  }

  /**
   * Update worker tolerance after manager decision
   */
  static updateWorkerTolerance(workerId, currentMonth, workerTolerances, deviationAmount, decision) {
    const toleranceKey = `${workerId}_${currentMonth}`;
    const tolerance = workerTolerances[toleranceKey] || {
      monthlyLimit: 0,
      currentUsed: 0,
      month: currentMonth
    };
    
    if (decision === 'rejected') {
      // Add to tolerance only if rejected
      tolerance.currentUsed += Math.abs(deviationAmount);
    }
    // If accepted, don't add to tolerance
    
    return {
      ...workerTolerances,
      [toleranceKey]: tolerance
    };
  }

  /**
   * Get current month string (YYYY-MM format)
   */
  static getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  /**
   * Create audit trail entry
   */
  static createAuditEntry(action, details) {
    return {
      id: `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      action,
      details
    };
  }

  /**
   * Process manager decision on deviation
   */
  static processManagerDecision(deviationId, decision, managerId, comments = '') {
    return {
      deviationId,
      decision, // 'accepted' or 'rejected'
      managerId,
      comments,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Get deviation statistics for reporting
   */
  static getDeviationStats(discrepancyManagement, dateRange = null) {
    const { selfDeviations, handoverDeviations } = discrepancyManagement;
    
    let filteredSelfDeviations = selfDeviations;
    let filteredHandoverDeviations = handoverDeviations;
    
    if (dateRange) {
      const { startDate, endDate } = dateRange;
      const filterByDate = (deviation) => {
        const deviationDate = new Date(deviation.timestamp);
        return deviationDate >= startDate && deviationDate <= endDate;
      };
      
      filteredSelfDeviations = selfDeviations.filter(filterByDate);
      filteredHandoverDeviations = handoverDeviations.filter(filterByDate);
    }
    
    const totalSelfDeviations = filteredSelfDeviations.length;
    const totalHandoverDeviations = filteredHandoverDeviations.length;
    const totalDeviations = totalSelfDeviations + totalHandoverDeviations;
    
    const acceptedSelf = filteredSelfDeviations.filter(d => d.status === 'accepted').length;
    const rejectedSelf = filteredSelfDeviations.filter(d => d.status === 'rejected').length;
    const pendingSelf = filteredSelfDeviations.filter(d => d.status === 'pending').length;
    
    const acceptedHandover = filteredHandoverDeviations.filter(d => d.status === 'accepted').length;
    const rejectedHandover = filteredHandoverDeviations.filter(d => d.status === 'rejected').length;
    const pendingHandover = filteredHandoverDeviations.filter(d => d.status === 'pending').length;
    
    const totalAmount = [
      ...filteredSelfDeviations,
      ...filteredHandoverDeviations
    ].reduce((sum, d) => sum + Math.abs(d.deviation.difference), 0);
    
    return {
      totalDeviations,
      totalSelfDeviations,
      totalHandoverDeviations,
      acceptedSelf,
      rejectedSelf,
      pendingSelf,
      acceptedHandover,
      rejectedHandover,
      pendingHandover,
      totalAccepted: acceptedSelf + acceptedHandover,
      totalRejected: rejectedSelf + rejectedHandover,
      totalPending: pendingSelf + pendingHandover,
      totalAmount
    };
  }

  /** id + code pour matcher sessions et écarts persistés. */
  static resolveWorkerMatchKeys(workerId, workers = []) {
    const keys = new Set();
    const raw = String(workerId ?? '').trim();
    if (raw) keys.add(raw);

    for (const worker of workers) {
      if (!worker) continue;
      const id = String(worker.id ?? '').trim();
      const code = String(worker.code ?? '').trim();
      if ((id && id === raw) || (code && code === raw)) {
        if (id) keys.add(id);
        if (code) keys.add(code);
      }
    }
    return keys;
  }

  static deviationTimestamp(deviation) {
    const raw = deviation?.timestamp || deviation?.sessionId;
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isFinite(date.getTime()) ? date : null;
  }

  static signedDeviationAmount(deviation) {
    const nested = deviation?.deviation;
    if (nested && typeof nested === 'object') {
      // totalDifference = écart session complet (espèces + électronique) — identique à la déconnexion
      if (nested.totalDifference != null && Number.isFinite(Number(nested.totalDifference))) {
        return Number(nested.totalDifference);
      }
      if (nested.difference != null && Number.isFinite(Number(nested.difference))) {
        return Number(nested.difference);
      }
    }
    return Number(deviation?.amount) || 0;
  }

  static workerMatchesDeviation(deviation, workerKeys) {
    if (!workerKeys?.size || !deviation) return false;
    const ids = [
      deviation.workerId,
      deviation.previousWorkerId,
      deviation.nextWorkerId
    ]
      .map((value) => String(value ?? '').trim())
      .filter(Boolean);
    return ids.some((id) => workerKeys.has(id));
  }

  static monthBoundsForDate(referenceDate = new Date()) {
    const date = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
    const safe = Number.isFinite(date.getTime()) ? date : new Date();
    const startDate = new Date(safe.getFullYear(), safe.getMonth(), 1);
    const endDate = new Date(safe.getFullYear(), safe.getMonth() + 1, 0, 23, 59, 59, 999);
    return { startDate, endDate };
  }

  static deviationBelongsToSession(deviation, session) {
    if (!deviation || !session) return false;
    const sessionIds = [session.loginTime, session.id]
      .filter((value) => value != null && value !== '')
      .map((value) => String(value));
    const deviationSessionId = String(deviation.sessionId ?? '').trim();
    if (deviationSessionId && sessionIds.includes(deviationSessionId)) {
      return true;
    }

    const deviationDate = DeviationService.deviationTimestamp(deviation);
    if (!deviationDate || !session.loginTime) return false;
    const loginMs = new Date(session.loginTime).getTime();
    const logoutMs = session.logoutTime
      ? new Date(session.logoutTime).getTime()
      : Date.now();
    const t = deviationDate.getTime();
    return t >= loginMs - 60000 && t <= logoutMs + 60000;
  }

  static sumDeviationAmounts(deviations = []) {
    return deviations.reduce(
      (sum, deviation) => sum + DeviationService.signedDeviationAmount(deviation),
      0
    );
  }

  /**
   * Écarts du mois cumulés jusqu'à cette session (session 8 = écarts 1…8, pas tout le mois répété).
   */
  static getCumulativeMonthlyEcarts(session, allSessions, discrepancyManagement, options = {}) {
    const empty = {
      count: 0,
      totalAmount: 0,
      sessionAmount: 0,
      sessionCount: 0,
      previousCumulative: 0,
      previousCount: 0
    };
    if (!session) return empty;

    const workers = Array.isArray(options.workers) ? options.workers : [];
    const { startDate, endDate } = DeviationService.monthBoundsForDate(session.loginTime);
    const workerKeys = DeviationService.resolveWorkerMatchKeys(session.workerId, workers);

    const sameWorkerMonthSessions = (allSessions || [])
      .filter((candidate) => {
        if (!candidate?.loginTime) return false;
        const login = new Date(candidate.loginTime);
        if (login < startDate || login > endDate) return false;
        const candidateKeys = DeviationService.resolveWorkerMatchKeys(candidate.workerId, workers);
        for (const key of candidateKeys) {
          if (workerKeys.has(key)) return true;
        }
        return false;
      })
      .sort((a, b) => new Date(a.loginTime) - new Date(b.loginTime));

    const sessionIndex = sameWorkerMonthSessions.findIndex(
      (candidate) =>
        String(candidate.loginTime) === String(session.loginTime) &&
        String(candidate.workerId) === String(session.workerId)
    );
    const throughSessions =
      sessionIndex >= 0 ? sameWorkerMonthSessions.slice(0, sessionIndex + 1) : [session];
    const beforeSessions = sessionIndex > 0 ? sameWorkerMonthSessions.slice(0, sessionIndex) : [];

    const allDeviations = [
      ...(discrepancyManagement?.selfDeviations || []),
      ...(discrepancyManagement?.handoverDeviations || [])
    ];

    const collectForSessions = (sessionList) => {
      const seen = new Set();
      const matched = [];
      for (const sess of sessionList) {
        for (const deviation of allDeviations) {
          if (!DeviationService.workerMatchesDeviation(deviation, workerKeys)) continue;
          if (!DeviationService.deviationBelongsToSession(deviation, sess)) continue;
          const key = String(
            deviation.id ||
              `${deviation.type}_${deviation.workerId}_${deviation.timestamp}_${deviation.sessionId}`
          );
          if (seen.has(key)) continue;
          seen.add(key);
          matched.push(deviation);
        }
      }
      return matched;
    };

    const throughDeviations = collectForSessions(throughSessions);
    const beforeDeviations = collectForSessions(beforeSessions);
    const sessionDeviations = collectForSessions([session]);

    return {
      count: throughDeviations.length,
      totalAmount: DeviationService.sumDeviationAmounts(throughDeviations),
      sessionAmount: DeviationService.sumDeviationAmounts(sessionDeviations),
      sessionCount: sessionDeviations.length,
      previousCumulative: DeviationService.sumDeviationAmounts(beforeDeviations),
      previousCount: beforeDeviations.length
    };
  }

  /**
   * Écarts du mois — live aus (Final − Soll) pro Session, konsistent mit Écart-Soll-Spalte.
   */
  static getCumulativeMonthlyLiveEcarts(session, allSessions, getSessionEcart, options = {}) {
    const empty = {
      count: 0,
      totalAmount: 0,
      sessionAmount: 0,
      sessionHasEcart: false,
      previousCumulative: 0,
      previousCount: 0
    };
    if (!session || typeof getSessionEcart !== 'function') return empty;

    const TOL = 0.01;
    const workers = Array.isArray(options.workers) ? options.workers : [];
    const { startDate, endDate } = DeviationService.monthBoundsForDate(session.loginTime);
    const workerKeys = DeviationService.resolveWorkerMatchKeys(session.workerId, workers);

    const sameWorkerMonthSessions = (allSessions || [])
      .filter((candidate) => {
        if (!candidate?.loginTime) return false;
        const login = new Date(candidate.loginTime);
        if (login < startDate || login > endDate) return false;
        const candidateKeys = DeviationService.resolveWorkerMatchKeys(candidate.workerId, workers);
        for (const key of candidateKeys) {
          if (workerKeys.has(key)) return true;
        }
        return false;
      })
      .sort((a, b) => new Date(a.loginTime) - new Date(b.loginTime));

    const sessionIndex = sameWorkerMonthSessions.findIndex(
      (candidate) =>
        String(candidate.loginTime) === String(session.loginTime) &&
        String(candidate.workerId) === String(session.workerId)
    );
    const throughSessions =
      sessionIndex >= 0 ? sameWorkerMonthSessions.slice(0, sessionIndex + 1) : [session];

    let totalAmount = 0;
    let sessionAmount = 0;
    let count = 0;

    for (const candidate of throughSessions) {
      const ecart = Number(getSessionEcart(candidate)) || 0;
      totalAmount += ecart;
      if (
        String(candidate.loginTime) === String(session.loginTime) &&
        String(candidate.workerId) === String(session.workerId)
      ) {
        sessionAmount = ecart;
      }
      if (Math.abs(ecart) > TOL) count += 1;
    }

    return {
      count,
      totalAmount,
      sessionAmount,
      sessionHasEcart: Math.abs(sessionAmount) > TOL,
      previousCumulative: totalAmount - sessionAmount,
      previousCount: Math.max(0, count - (Math.abs(sessionAmount) > TOL ? 1 : 0))
    };
  }

  /**
   * Get worker-specific deviation statistics
   * @param {object} [dateRange] — { startDate, endDate, workers? }
   */
  static getWorkerDeviationStats(workerId, discrepancyManagement, dateRange = null) {
    const { selfDeviations, handoverDeviations } = discrepancyManagement || {};
    const workers = Array.isArray(dateRange?.workers) ? dateRange.workers : [];
    const workerKeys = DeviationService.resolveWorkerMatchKeys(workerId, workers);

    let filteredSelfDeviations = (selfDeviations || []).filter((d) =>
      workerKeys.has(String(d.workerId ?? '').trim())
    );
    let filteredHandoverDeviations = (handoverDeviations || []).filter((d) =>
      DeviationService.workerMatchesDeviation(d, workerKeys)
    );

    if (dateRange?.startDate && dateRange?.endDate) {
      const { startDate, endDate } = dateRange;
      const filterByDate = (deviation) => {
        const deviationDate = DeviationService.deviationTimestamp(deviation);
        if (!deviationDate) return false;
        return deviationDate >= startDate && deviationDate <= endDate;
      };

      filteredSelfDeviations = filteredSelfDeviations.filter(filterByDate);
      filteredHandoverDeviations = filteredHandoverDeviations.filter(filterByDate);
    }

    const allDeviations = [...filteredSelfDeviations, ...filteredHandoverDeviations];
    const totalDeviations = allDeviations.length;
    const totalAmount = allDeviations.reduce(
      (sum, d) => sum + DeviationService.signedDeviationAmount(d),
      0
    );

    return {
      workerId,
      totalDeviations,
      selfDeviations: filteredSelfDeviations.length,
      handoverDeviations: filteredHandoverDeviations.length,
      totalAmount,
      deviations: allDeviations
    };
  }
}

export default DeviationService;
